// 總覽（SPEC #vms-web-827 HEALTH-01「總覽的數字與版面」，VMS UX 評審 2026-10-13 P0-8、P1-1）——三段：
//   ① 需要處理：離線、儲存異常、憑證快到期、有新版、未處理警報——只列不是 0 的，每列一個動作
//   ② 健康：卡片，每張都是連結（帶篩選網址參數）；數字跟設備清單 / 韌體頁 / 頁首同一套定義（lib/devices.ts countDevices）
//   ③ 警報：有還沒處理的就列最新 5 則未處理（第二輪 R2-P1-1、B2-08，不跟「需要處理：N 則警報」矛盾），
//      沒有才列最近的警報（level = alarm）；事件顯示名 + 完整設備名 + 縮圖 +〔回放〕
//   健康卡：韌體 0 台要更新寫「全部最新（N 台）」；錄影缺口卡連回放；雲錄席次卡另寫家戶方案提供的台數（R2-P1-7）
// 下面是家戶清單（角色用譯名、布防模式）。載入中卡片用骨架，不先顯示 0 或「—」（UX-04）。
import { useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getShadow } from '@kinio/web-sdk';
import { useTranslation } from 'react-i18next';
import { storageHealth, type StorageHealth } from '../../lib/deviceApi';
import { smoothRetention } from '../../lib/bitrateAvg';
import { deviceHref, StorageSummary } from '../devices/DeviceDetailPage';
import { homeLabel } from '../devices/useHomeDetails';
import { useDevices } from '../../lib/useDevices';
import { useRecentEvents } from '../../lib/useRecentEvents';
import { formatDateTime } from '../../lib/tz';
import { sinceStamp } from '../../lib/timefmt';
import { groupAlarms } from '../../lib/alarmGroup';
import { compareTidy, openAgeText, recoveredAt, tidyRank } from '../../lib/alarmTidy';
import { useNow } from '../../lib/useNow';
import { useScope } from '../../lib/scope';
import { isOrgAdmin } from '../../lib/planeApi';
import { useTzResolver } from '../../lib/useTz';
import { countDevices, eventDeviceLabel, fullName, type VmsDevice } from '../../lib/devices';
import { isRecorderHost } from '../playback/view';
import { classify, EpisodeNote } from '../events/EventsPage';
import type { Episode } from '../../lib/eventEpisodes';
import { useHealth, Kpi } from './HealthCards';
import { ArmedControl } from './ArmedControl';
import { eventName } from '../../lib/eventName';
import { playbackHref } from '../../lib/links';
import { useAlarmStates } from '../../lib/alarmStates';
import { EventThumb } from '../../components/EventThumb';
import { KmgrIncidentsBlock, KmgrOverviewColumns } from '../kmgr/OverviewBlocks';
import { CHILD_SEP } from '../../lib/devices';
import { homePlanSentence, useHomePlanCloud } from '../billing/homePlanCloud';
import { retentionText } from '../../lib/duration';

/** 保存天數的算法說明：「以最近平均錄影 1.36 Mbps、15.5 GB 循環覆寫估算」（平均位元率，第六輪 B6-07）。 */
export function retentionBasis(t: (k: string, o?: Record<string, unknown>) => string, s: Pick<StorageHealth, 'recordKbps' | 'avgKbps' | 'totalBytes' | 'freeBytes' | 'overwriteMode'>): string {
  const kbps = s.avgKbps ?? s.recordKbps;
  if (!kbps) return '';
  const mbps = (kbps / 1000).toFixed(2).replace(/\.?0+$/, '');
  const off = s.overwriteMode?.kind === 'off';
  const bytes = off ? s.freeBytes : s.totalBytes;
  if (!bytes) return '';
  const gb = (bytes / 1e9).toFixed(1).replace(/\.0$/, '');
  return t(off ? 'overview.basis_off' : 'overview.basis_auto', { mbps, gb });
}

/**
 * 值班帳號（SPEC #vms-web-827「值班版面」）：
 *   - 目前範圍裡任一組織對我開了值班模式（AUTH-08 `duty_mode`，GET /v1/me/orgs）→ 一律是（管理者也一樣；app/vms#19 TH-08）
 *   - 否則：能處理警報（任一家戶 can_handle），而且不是管理者——範圍內的家戶沒有 owner / admin、
 *     這些家戶所屬的組織沒有 owner / org_admin（個人組織沒有看得到的家戶就不算）。
 */
/** 目前範圍內任一組織對我開了值班模式（AUTH-08 `duty_mode`）：值班版面一律、打開 VMS 預設進警報工作台（SPEC #vms-web-alarm-workbench）。 */
export function hasDutyMode(visible: { org_id?: string }[], orgs: { org_id: string; duty_mode?: boolean }[]): boolean {
  const ids = new Set(visible.map((h) => h.org_id).filter((x): x is string => !!x));
  return orgs.some((o) => ids.has(o.org_id) && !!o.duty_mode);
}

export function isDutyAccount(
  visible: { home_id: string; role?: string; org_id?: string }[],
  orgs: { org_id: string; role: string; duty_mode?: boolean }[],
  canHandle: (homeId: string) => boolean,
): boolean {
  if (hasDutyMode(visible, orgs)) return true;
  const ids = new Set(visible.map((h) => h.org_id).filter((x): x is string => !!x));
  if (!visible.some((h) => canHandle(h.home_id))) return false;
  if (visible.some((h) => h.role === 'owner' || h.role === 'admin')) return false;
  return !orgs.some((o) => ids.has(o.org_id) && isOrgAdmin(o));
}

/** 這次打開 VMS 的第一個網址（值班帳號只在「打開 VMS 就是根網址」時直接進工作台；之後從選單回總覽照常）。 */
const ENTRY_PATH = typeof window === 'undefined' ? '' : window.location.pathname;
let dutyLandingChecked = false;

export function OverviewPage() {
  const { t, i18n } = useTranslation();
  const { results, tree, cameras, loading } = useDevices();
  const recent = useRecentEvents(200, 15_000);
  const tzOf = useTzResolver();
  const { unhandled, unhandledMore, hasUnhandled, openList, openReady, listed, unlistedOpen, canHandle } = useAlarmStates();
  const { visible, orgs } = useScope();
  const now = useNow(60_000);
  // 值班版面（SPEC #vms-web-827「值班版面」）：開了值班模式（AUTH-08）的帳號一律；其餘是能處理警報、又不是管理者（組織 owner / org_admin、家戶 owner / admin）→ 警報區放在健康卡前面
  const duty = isDutyAccount(visible, orgs, canHandle);
  // 值班模式（AUTH-08）帳號打開 VMS（登入後或直接開根網址）預設進警報工作台（SPEC #vms-web-alarm-workbench）
  const nav = useNavigate();
  const scopeLoading = useScope().loading;
  useEffect(() => {
    if (dutyLandingChecked || scopeLoading || !orgs.length) return;
    dutyLandingChecked = true;
    if (ENTRY_PATH.replace(/\/+$/, '') === '' && hasDutyMode(visible, orgs)) nav('/events?tab=alarms', { replace: true });
  }, [scopeLoading, orgs, visible, nav]);
  const alarmsText = (n: number) => t(unhandledMore ? 'overview.todo_alarms_more' : 'overview.todo_alarms', { count: n });
  const byKey = new Map(tree.flatMap((d) => [d, ...d.children]).map((d) => [d.key, d]));
  const latest = (recent.data ?? [])
    .filter((r) => ((r.ev as { level?: string }).level ?? classify(r.ev.type, r.ev.event_class as string | undefined)) === 'alarm')
    .slice(0, 6);
  const counts = countDevices(tree);
  const camsOnline = cameras.filter((c) => c.presence === 'online').length;
  // 錄影主機（NVR / Kinio Station；工位模式不算）逐台讀 shadow 的 kinio_storage
  const recorders = tree.filter(isRecorderHost);
  const shadows = useQueries({
    queries: recorders.map((d) => ({
      queryKey: ['shadow', d.homeId, d.aid],
      queryFn: () => getShadow(d.apiEndpoint, d.homeId, d.aid),
      staleTime: 30_000,
      refetchInterval: 60_000,
      retry: false,
    })),
  });
  const storages = recorders
    // 預估保存天數用 24 小時平均位元率（跟設備頁同一個值，不隨當下位元率跳；第六輪 B6-07）
    .map((d, i) => ({ d, s: smoothRetention(d.key, storageHealth(shadows[i]?.data?.state?.reported?.kinio_storage as Record<string, unknown> | undefined)) }))
    .filter((x): x is { d: VmsDevice; s: StorageHealth } => !!x.s);
  const storageBad = storages.filter((x) => x.s.level === 'alert' || x.s.level === 'warn');
  const storLoading = loading || shadows.some((q) => q.isLoading);
  const minRec = storages.filter((x) => x.s.retentionDays !== undefined).sort((a, b) => (a.s.retentionDays ?? 0) - (b.s.retentionDays ?? 0))[0];
  const minRetention = minRec ? retentionText(t, minRec.s.retentionDays ?? 0) : null;
  const offlineList = tree.filter((d) => d.presence === 'offline');
  const h = useHealth(tree, loading);
  // 雲錄席次卡：席次以外由家戶方案提供雲錄的攝影機（R2-P1-7；只在有席次卡時查）
  const hp = useHomePlanCloud(cameras, !!h.seat && !loading);
  const hpNote = hp.list.length ? t('health.seats_home_plan', { count: hp.list.length }) : undefined;
  const hpTitle = homePlanSentence(t, hp.list, 3, (d) => fullName(d, tree)) || undefined;
  // 警報段：有未處理的列最新 5 則未處理；沒有才列最近的警報
  type AlarmRow = { id: string; homeId: string; deviceKey: string; parentKey: string; ts: number; name: string; key?: string; times?: number; since?: number; ep?: Episode };
  // 還沒處理的同一台＋同一種合成一列「× 4 次（最早 13:47）」（跟事件清單、鈴鐺同一個規則；第五輪 B5-10）
  // openList 已是一次一列（開始＋結束合一，時間 = 開始時間；第九輪 B9-01、B9-04），次數跟清單、facet 同一個單位
  // 未登記 / 已移除設備的不列、不算（最後一行寫「另有 N 則…」）；排序：安防組 → 狀態組還沒恢復 → 已恢復，同組開最久的在前（SPEC #vms-web-alarm-tidy）
  const openGroups = groupAlarms(
    openList.filter((a) => listed(a.home_id, a.device_ref)),
    (a) => `${a.home_id}/${a.device_ref}/${a.key}`,
    (a) => Date.parse(a.occurred_at) || 0,
    { open: () => true },
  );
  const tidyKey = (g: (typeof openGroups)[number]) => {
    const ts = Date.parse(g.head.occurred_at) || 0;
    const oldest = g.rest.length ? Date.parse(g.rest[g.rest.length - 1].occurred_at) || 0 : ts;
    return { rank: tidyRank({ state: 'open', recovered: recoveredAt(g.head.key, g.head.ep) !== null, listed: true, key: g.head.key }), earliest: Math.min(ts, oldest), ts, ref: g.head.device_ref, key: g.head.key, id: g.head.event_id };
  };
  openGroups.sort((a, b) => compareTidy(tidyKey(a), tidyKey(b)));
  const openRows: AlarmRow[] = openGroups.slice(0, 5).map(({ head: a, rest }) => {
    const child = a.device_ref.includes(CHILD_SEP);
    return {
      id: a.event_id,
      homeId: a.home_id,
      deviceKey: `${a.home_id}/${a.device_ref}`,
      parentKey: `${a.home_id}/${child ? a.device_ref.slice(0, a.device_ref.lastIndexOf(CHILD_SEP)) : a.device_ref}`,
      ts: Date.parse(a.occurred_at) || 0,
      name: eventName(t, a.key, a.class),
      key: a.key,
      ep: a.ep,
      ...(rest.length ? { times: rest.length + 1, since: Date.parse(rest[rest.length - 1].occurred_at) || 0 } : {}),
    };
  });
  const openShown = openGroups.slice(0, 5).reduce((n, g) => n + 1 + g.rest.length, 0);
  const recentRows: AlarmRow[] = latest.map((r) => ({
    id: r.ev.id,
    homeId: r.deviceKey.split('/')[0],
    deviceKey: r.deviceKey,
    parentKey: r.parentKey,
    ts: r.ts,
    name: eventName(t, r.ev.type, r.ev.event_class as string | undefined, r.ev.payload as Record<string, unknown> | undefined, r.ev.device_ref),
  }));
  const showOpen = openRows.length > 0;
  const alarmRows = showOpen ? openRows : recentRows;
  const val = (_n: number) => (loading ? ({ kind: 'loading' } as const) : ({ kind: 'value' } as const));

  // ① 需要處理（只列不是 0 的）
  const names = (ds: VmsDevice[]) => ds.slice(0, 3).map((d) => d.name).join(t('common.list_sep')) + (ds.length > 3 ? t('overview.and_more', { count: ds.length - 3 }) : '');
  const todo: { id: string; text: string; to: string; action: string }[] = [];
  if (!loading && counts.offlineDevices + counts.offlineChannels > 0)
    todo.push({ id: 'offline', text: t('overview.todo_offline', { devices: counts.offlineDevices, channels: counts.offlineChannels }) + (offlineList.length ? ' · ' + names(offlineList) : ''), to: '/devices?status=offline', action: t('overview.act_view') });
  if (storageBad.length)
    todo.push({ id: 'storage', text: t('overview.todo_storage', { count: storageBad.length }) + ' · ' + names(storageBad.map((x) => x.d)), to: deviceHref(storageBad[0].d) + '?tab=recording', action: t('overview.act_check') });
  if (h.certs.length) todo.push({ id: 'cert', text: t('overview.todo_cert', { count: h.certs.length }) + ' · ' + names(h.certs.map((x) => x.d)), to: '/devices?cert=expiring', action: t('overview.act_view') });
  if (h.fwOutdated.length)
    todo.push({ id: 'firmware', text: t('overview.todo_fw', { count: h.fwOutdated.length }) + ' · ' + h.fwOutdated.slice(0, 3).map((r) => t('overview.fw_item', { name: r.name, v: r.s.current_version ?? '?' })).join(t('common.list_sep')), to: '/devices/firmware?status=outdated', action: t('overview.act_update') });
  if (unhandled) todo.push({ id: 'alarms', text: alarmsText(unhandled), to: '/events', action: t('overview.act_handle') });
  // 次數還在算（舊平台要等完整清單）：平台說有未處理的就先留一行「計算中」，不整行拿掉（第十輪 B10-01：值班的人會以為沒有警報）
  else if (unhandled === null && hasUnhandled) todo.push({ id: 'alarms', text: t('overview.todo_alarms_counting'), to: '/events', action: t('overview.act_handle') });
  const gapHref = h.gapCams[0] ? playbackHref(h.gapCams[0].key, h.gapAt.get(h.gapCams[0].key)) : '/playback';
  // 本地缺口的攝影機名後面寫主要原因（「月台（錄影主機重開機）」；SPEC #vms-web-827「原因的顯示」）
  const gapNames = (ds: VmsDevice[]) =>
    ds
      .slice(0, 3)
      .map((d) => {
        const r = h.gapReason.get(d.key);
        return r ? t('overview.gap_cam_reason', { name: d.name, reason: t(`health.gap_reason_${r}`) }) : d.name;
      })
      .join(t('common.list_sep')) + (ds.length > 3 ? t('overview.and_more', { count: ds.length - 3 }) : '');
  if (h.gapCams.length) todo.push({ id: 'rec-gap', text: t('overview.todo_gaps', { count: h.gapCams.length }) + ' · ' + gapNames(h.gapCams), to: gapHref, action: t('overview.act_playback') });

  const firstBad = storageBad[0]?.d;
  // 警報區（值班版面放在健康卡前面）
  const alarmSection = (
    <>
        <h2 className="section-title" data-testid="overview-alarms-title">
          {showOpen ? t('overview.open_alarms') : t('overview.recent_alarms')}
          {showOpen && unhandled ? <span className="muted small" data-testid="overview-alarms-count"> · {alarmsText(unhandled)}</span> : null}
        </h2>
        <ul className="event-list" data-testid={showOpen ? 'overview-open-alarms' : 'overview-recent-alarms'}>
          {alarmRows.map((r) => {
            const d = byKey.get(r.deviceKey) ?? byKey.get(r.parentKey);
            // 跟警報分頁同一個設備標籤（含通道、「未登記的通道」；第七輪 B7-02）
            const name = eventDeviceLabel(t, (ref) => byKey.get(`${r.homeId}/${ref}`), tree, r.deviceKey.slice(r.homeId.length + 1));
            return (
              <li key={`${r.deviceKey}/${r.id}`} className="event-row cls-alarm" data-alarm={r.id}>
                <EventThumb api={d?.apiEndpoint} homeId={r.homeId} device={r.deviceKey.slice(r.homeId.length + 1)} atMs={r.ts} alt={name} className="thumb-slot" verify />
                <div className="event-main">
                  <Link to={`/events/${encodeURIComponent(r.homeId)}/${encodeURIComponent(r.id)}`}>
                    <strong>{r.name}</strong>
                  </Link>
                  <span className="muted"> · {name}</span>
                  {/* 跟事件清單同一列寫法：開始時間＋持續 / 進行中（第九輪 B9-04） */}
                  {r.ep && <EpisodeNote ep={r.ep} eventKey={r.key} tz={tzOf(r.homeId)} />}
                  <div className="muted small">
                    {formatDateTime(r.ts / 1000, tzOf(r.homeId), i18n.language)}
                    {showOpen && (
                      <span data-testid="alarm-age">
                        {' · '}
                        {openAgeText(t, Math.min(r.ts, r.since ?? r.ts), now)}
                      </span>
                    )}
                    {r.times && r.since !== undefined && (
                      <span data-testid="overview-alarm-times" data-counting={!openReady ? '1' : undefined}>
                        {' · '}
                        {/* 完整的未處理清單還沒到：不先寫最近 200 則算出來的次數（第八輪 B8-03） */}
                        {!openReady
                          ? t('events.times_counting')
                          : !sinceStamp(r.since, r.ts, tzOf(r.homeId))
                          ? t('events.times_only', { n: r.times })
                          : t('events.times_n', { n: r.times, since: sinceStamp(r.since, r.ts, tzOf(r.homeId)) })}
                      </span>
                    )}
                  </div>
                </div>
                {d && (
                  <Link className="btn btn-sm" to={playbackHref(d.key, r.ts / 1000, { event: r.id })}>
                    {t('live.watch_playback')}
                  </Link>
                )}
              </li>
            );
          })}
          {showOpen && unhandled && unhandled > openShown ? (
            <li className="muted small">
              <Link to="/events">{t('overview.open_alarms_more', { count: unhandled })}</Link>
            </li>
          ) : null}
          {unlistedOpen > 0 && (
            <li className="muted small overview-unlisted" data-testid="overview-unlisted">
              {t('overview.unlisted_open', { count: unlistedOpen })}{' '}
              <Link to="/events?tab=alarms">{t('overview.act_view')}</Link>
            </li>
          )}
          {!showOpen && !recent.isLoading && recentRows.length === 0 && (
            // 「需要處理：N 則警報還沒處理」與「最近沒有警報」不能同頁互相矛盾（第二輪 B2-08）
            <li className="muted" data-testid="overview-no-recent-alarms">
              {unhandled ? (
                <>
                  {t('overview.no_new_alarms_open', { count: unhandled })}{' '}
                  <Link className="btn btn-sm" to="/events">
                    {t('overview.act_handle')}
                  </Link>
                </>
              ) : (
                t('overview.no_alarms')
              )}
            </li>
          )}
        </ul>
    </>
  );
  return (
    <section className="overview">
      <h1 className="page-title">{t('nav.overview')}</h1>
      {/* Kinio KMS：有 kmgr.read 時原版面放左 8 欄、右 4 欄放 IT 區塊；沒有時原樣（SPEC #kinio-kms-ui 總覽版型） */}
      <KmgrOverviewColumns>

      <h2 className="section-title">{t('overview.todo_title')}</h2>
      {loading ? (
        <div className="card skeleton-block" aria-busy="true" />
      ) : todo.length ? (
        <ul className="plain-list todo-list card" aria-label={t('overview.todo_title')}>
          {todo.map((x) => (
            <li key={x.id} data-attention={x.id}>
              <span>{x.text}</span>
              <Link className="btn btn-sm" to={x.to}>
                {x.action}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="card muted" data-testid="todo-none">
          {t('overview.todo_none')}
        </p>
      )}
      <KmgrIncidentsBlock />
      {duty && alarmSection}

      <h2 className="section-title">{t('health.title')}</h2>
      <div className="kpis">
        <Kpi
          id="devices"
          label={t('overview.devices')}
          to="/devices"
          state={val(counts.devices)}
          value={t('overview.devices_n', { count: counts.devices })}
          note={counts.channels ? t('overview.channels_note', { count: counts.channels }) : undefined}
          tip={t('overview.devices_tip')}
        />
        <Kpi
          id="offline"
          label={t('overview.offline')}
          to="/devices?status=offline"
          state={val(0)}
          value={
            // 卡片上寫短的（第五輪 R5-P2-5：「1 台設備、3 個通道」在卡片上換兩行）；完整說法在「需要處理」那一列
            counts.offlineDevices && counts.offlineChannels
              ? t('overview.offline_short', { devices: counts.offlineDevices, channels: counts.offlineChannels })
              : counts.offlineDevices
                ? t('overview.offline_dev_only', { count: counts.offlineDevices })
                : counts.offlineChannels
                  ? t('overview.offline_ch_only', { count: counts.offlineChannels })
                  : '0'
          }
          note={counts.devices ? t('overview.online_rate', { pct: Math.round((counts.onlineDevices / counts.devices) * 100) }) : undefined}
          tip={t('overview.offline_tip')}
          small={counts.offlineDevices + counts.offlineChannels > 0}
          tone={counts.offlineDevices + counts.offlineChannels ? 'err' : undefined}
        />
        <Kpi id="cameras" label={t('overview.cameras')} to="/live" state={val(0)} value={`${camsOnline} / ${cameras.length}`} tip={t('overview.cameras_tip')} />
        {(recorders.length > 0 || loading) && (
          <Kpi
            id="storage-bad"
            label={t('health.recorders')}
            to={firstBad ? deviceHref(firstBad) + '?tab=recording' : '/devices?type=recorder'}
            state={storLoading ? { kind: 'loading' } : storages.length ? { kind: 'value' } : { kind: 'unknown', missing: recorders.length }}
            value={storageBad.length ? t('overview.recorders_bad', { bad: storageBad.length, total: storages.length }) : t('overview.recorders_ok', { count: storages.length })}
            tip={t('overview.recorders_tip')}
            tone={storageBad.length ? 'err' : undefined}
          />
        )}
        {(recorders.length > 0 || loading) && (
          <Kpi
            id="retention"
            label={t('health.min_retention')}
            to={minRec ? deviceHref(minRec.d) + '?tab=recording' : '/devices?type=recorder'}
            state={storLoading ? { kind: 'loading' } : minRec ? { kind: 'value' } : { kind: 'unknown' }}
            // 不到 1 天寫「不到 1 天（約 N 小時）」、警示色，不寫「0 天」（第十二輪 B12-05）
            value={minRetention?.text ?? ''}
            tone={minRetention?.short ? 'warn' : undefined}
            small={minRetention?.short}
            note={minRec ? retentionBasis(t, minRec.s) : undefined}
          />
        )}
        <Kpi
          id="firmware"
          label={t('health.firmware')}
          to="/devices/firmware?status=outdated"
          state={h.fw}
          value={
            h.fwOutdated.length
              ? t('overview.fw_outdated', { count: h.fwOutdated.length })
              : h.fwTotal - h.fwUnknown > 0
                ? t('overview.fw_all_latest_card', { count: h.fwTotal - h.fwUnknown })
                : '0'
          }
          small={!h.fwOutdated.length && h.fwTotal - h.fwUnknown > 0}
          note={h.fwUnknown ? t('overview.fw_unknown_note', { count: h.fwUnknown }) : undefined}
          tip={t('overview.fw_tip')}
          tone={h.fwOutdated.length ? 'warn' : undefined}
        />
        <Kpi
          id="cert"
          label={t('health.cert')}
          to="/devices?cert=expiring"
          state={h.cert}
          value={String(h.certs.length)}
          note={t('health.cert_note', { count: 30 })}
          tone={h.certs.length ? 'err' : undefined}
        />
        {h.seat && (
          <Kpi
            id="seats"
            label={t('health.seats')}
            to="/billing#seats"
            setupTo="/billing#seats"
            state={h.seat}
            value={`${h.su.used} / ${h.su.total}`}
            note={hpNote ? `${t('health.seats_note')} · ${hpNote}` : t('health.seats_note')}
            offNote={hpNote}
            tip={hpTitle}
          />
        )}
        {h.ai && (
          <Kpi
            id="ai"
            label={t('health.ai')}
            to="/ai"
            setupTo="/ai"
            state={h.ai}
            value={`${h.aiUsed} / ${h.aiLimit}`}
            tone={h.aiLimit && h.aiUsed >= h.aiLimit ? 'err' : h.aiLimit && h.aiUsed >= h.aiLimit * 0.8 ? 'warn' : undefined}
          />
        )}
        {h.backup && (
          <Kpi
            id="backup"
            label={t('health.backup')}
            to="/backup"
            setupTo="/backup"
            state={h.backup}
            value={`${h.bks.ok} / ${h.bks.total}`}
            note={h.bks.degraded ? t('health.backup_degraded', { n: h.bks.degraded }) : t('health.backup_note')}
            tone={h.bks.degraded ? 'err' : undefined}
          />
        )}
        {h.gap && (
          <Kpi
            id="rec-gaps"
            label={t('health.rec_gaps')}
            to={gapHref}
            setupTo="/playback"
            setupLabel={t('health.watch_playback')}
            offNote={t('health.rec_gaps_off2')}
            state={h.gap}
            value={`${h.gapCams.length} / ${h.gapChecked}`}
            // 「過去 24 小時 · 雲端 A 台、本地 B 路」＋沒回報的（SPEC #vms-web-827「錄影缺口」）
            note={
              h.gap.kind !== 'value'
                ? undefined
                : t('health.rec_gaps_note2', { cloud: h.cloudChecked, local: h.localChecked }) +
              (h.localUnreported ? ' · ' + t('health.rec_gaps_unreported', { count: h.localUnreported }) : '')
            }
            tip={t('health.rec_gaps_note')}
            tone={h.gapCams.length ? 'warn' : undefined}
          />
        )}
      </div>
      {storages.length > 0 && (
        <ul className="plain-list health-list" aria-label={t('health.recorders')}>
          {storages.map(({ d, s }) => (
            <li key={d.key} data-storage={d.aid}>
              <Link to={deviceHref(d) + '?tab=recording'}>{d.name}</Link> <span className="muted">· {d.homeName}</span> <StorageSummary s={s} />
            </li>
          ))}
        </ul>
      )}

      {!duty && alarmSection}

      <h2 className="section-title">{t('overview.homes')}</h2>
      {results.length === 0 ? (
        <p className="card muted">{t('overview.no_homes')}</p>
      ) : (
        <div className="home-list">
          {results.map((r) => {
            const devs = tree.filter((d) => d.homeId === r.home.home_id);
            const on = devs.filter((d) => d.presence === 'online').length;
            return (
              <div key={r.home.home_id} className="card home-row">
                <span className="name">{homeLabel(r.home)}</span>
                <span className="badge">
                  {t('overview.role')}
                  {t('common.colon')}
                  {t(`people.hrole_${r.home.role || 'undefined'}`, { defaultValue: r.home.role })}
                </span>
                <span className="muted">{r.loading ? t('common.loading') : t('overview.home_online', { on, total: devs.length })}</span>
                <div className="armed-wrap">
                  <span className="muted small">{t('overview.armed_hint')}</span>
                  <ArmedControl home={r.home} current={(r.detail as { home?: { armed_mode?: string } } | undefined)?.home?.armed_mode} />
                </div>
                {r.error ? <span className="error">{t('common.error')}</span> : null}
                <Link className="btn" to="/devices">
                  {t('overview.open_devices')}
                </Link>
              </div>
            );
          })}
        </div>
      )}
      </KmgrOverviewColumns>
    </section>
  );
}
